import classNames from 'classnames';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import QrCode from '@/features/assistant/qr-code';
import { errorOf, useAssistant } from '@/features/assistant/use-assistant';
import type { SiteLeadResult } from '@/types/api';
import http from '@/utils/http';

/** The site's answers to "How did you hear about us?" (static/contact-us.html). */
const HEARD = ['Search engine', 'Social media', 'Referral or word of mouth', 'Industry event or conference', 'Advertising', 'Press or article', 'Other'];

const EMPTY = { first_name: '', last_name: '', email: '', phone: '', telegram: '', heard_from: '', message: '' };
type Form = typeof EMPTY;

const input =
  'h-12 w-full min-w-0 rounded-xl bg-site-field px-4 text-base text-site-ink placeholder:text-site-muted/80 transition focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand/60';
const card = 'rounded-2xl bg-white shadow-[0_1px_2px_rgb(0_0_0/0.04),0_12px_32px_-12px_rgb(0_0_0/0.12)]';

const MailIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const TelegramIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M21.9 4.3c.3-1.3-.5-1.8-1.4-1.5L2.6 9.7c-1.2.5-1.2 1.2-.2 1.5l4.6 1.4 1.8 5.4c.2.6.4.8.8.8.4 0 .6-.2.9-.4l2.2-2.1 4.6 3.4c.8.5 1.4.2 1.6-.8l3-14.6ZM8.4 12.4l9.9-6.2c.5-.3.9-.1.5.2l-8.2 7.4-.3 3.4-1.9-4.8Z" />
  </svg>
);

const Check = () => (
  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand" aria-hidden>
    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12 5 5 9-10" />
    </svg>
  </span>
);

const Channel = ({ icon, label, value, note, href, tone = 'brand' }: { icon: ReactNode; label: string; value: string; note?: string; href?: string; tone?: 'brand' | 'telegram' }) => {
  const external = href?.startsWith('http');
  const body = (
    <>
      <span
        className={classNames(
          'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          tone === 'telegram' ? 'bg-site-telegram/10 text-site-telegram' : 'bg-brand/10 text-brand',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[11px] uppercase tracking-[0.14em] text-site-muted">{label}</span>
        <span className="block truncate font-bold text-site-ink">{value}</span>
        {note && <span className="block text-xs text-site-muted">{note}</span>}
      </span>
    </>
  );
  return (
    <li>
      {href ? (
        <a
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className={classNames(card, 'flex items-center gap-4 px-5 py-4 transition hover:-translate-y-0.5 hover:shadow-[0_2px_4px_rgb(0_0_0/0.05),0_18px_40px_-14px_rgb(0_0_0/0.2)] active:translate-y-0')}
        >
          {body}
        </a>
      ) : (
        <div className={classNames(card, 'flex items-center gap-4 px-5 py-4')}>{body}</div>
      )}
    </li>
  );
};

const Field = ({ id, label, optional, error, children }: { id: string; label: string; optional?: boolean; error?: string; children: ReactNode }) => (
  <div className="min-w-0">
    <label htmlFor={id} className="mb-2 block text-sm font-bold text-site-ink">
      {label} {optional ? <span className="font-normal text-site-muted">(optional)</span> : <span className="text-brand" aria-hidden>*</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-err`} className="mt-1.5 text-xs text-danger">
        {error}
      </p>
    )}
  </div>
);

/** After the form: the site's "Message sent", and the way on to Assistant on Telegram. */
const Sent = ({ first, result, onAgain }: { first: string; result: SiteLeadResult; onAgain: () => void }) => (
  <div role="status" className="animate-pop-in">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-ok/15 text-ok" aria-hidden>
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="m5 12 5 5 9-10" />
      </svg>
    </span>
    <h3 className="mt-4 text-2xl font-bold text-site-ink">Message sent.</h3>
    <p className="mt-2 text-site-text">
      Thanks{first ? `, ${first}` : ''}. Your message is on its way to the team, and a person will get back to you within one business day.
    </p>

    <div className="relative mt-8 overflow-hidden rounded-2xl bg-site-ink p-6 text-white md:p-7">
      <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-site-telegram/40 blur-3xl" aria-hidden />
      <div className="relative flex flex-col gap-6 md:flex-row md:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-site-telegram">Can’t wait?</p>
          <h4 className="mt-2 text-xl font-bold">Continue on Telegram</h4>
          <p className="mt-2 text-sm text-white/70">
            Assistant, our AI bot, can answer many questions right away. A real person from our team will also join the group, for further guidance.
          </p>
          {result.link ? (
            <a
              href={result.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex h-12 items-center gap-2 rounded-full bg-site-telegram px-6 font-bold text-white shadow-[0_8px_24px_-8px_rgb(34_158_217/0.7)] transition hover:bg-white hover:text-site-ink active:translate-y-px"
            >
              <TelegramIcon />
              Message us on Telegram
            </a>
          ) : (
            <p className="mt-5 rounded-xl bg-white/10 px-4 py-3 text-sm text-white/80">Preview: {result.problem}</p>
          )}
        </div>
        {result.link && (
          <div className="hidden shrink-0 flex-col items-center gap-2 md:flex">
            <QrCode link={result.link} label="QR code: the same chat with Assistant, on your phone" className="w-36" />
            <p className="text-xs text-white/60">On a computer? Scan it with your phone.</p>
          </div>
        )}
      </div>
    </div>

    <button type="button" onClick={onAgain} className="mt-6 min-h-11 text-sm text-site-muted underline-offset-2 hover:text-site-ink hover:underline">
      Send another message
    </button>
  </div>
);

/**
 * Prisma's preview of whitelabels.com/contact-us (ASSIST-1), to try the website's way to
 * Assistant as a visitor: the site's contact form, made into a lead as the real form is, then
 * the thank-you page's Telegram button. Only where Assistant is on; outside Prisma's frame,
 * so it looks like the site.
 */
const SitePreviewPage = () => {
  const status = useAssistant();
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ first: string; result: SiteLeadResult } | null>(null);

  if (status.data && !status.data.enabled) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <StateMessage title="Not available here">Assistant is not turned on in this environment.</StateMessage>
      </div>
    );
  }

  const set = (k: keyof Form) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setAlert(null);
    try {
      const res = await http.post<SiteLeadResult>('/assistant/site-leads', form);
      setSent({ first: form.first_name.trim(), result: res.data });
      setForm(EMPTY);
    } catch (err) {
      const field = (err as { response?: { data?: { field?: string } } }).response?.data?.field as keyof Form | undefined;
      if (field && field in EMPTY) setErrors({ [field]: errorOf(err) });
      else setAlert(errorOf(err));
    } finally {
      setBusy(false);
    }
  };

  // After the form, the lead's own chat; before it, the website's.
  const telegram = sent?.result.link || status.data?.site_link || '';
  const described = (k: keyof Form) => (errors[k] ? { 'aria-invalid': true, 'aria-describedby': `cf-${k}-err` } : {});

  return (
    <div className="min-h-screen overflow-x-hidden bg-site-ink font-site text-white antialiased">
      {/* Prisma's note that this is a preview: the only part that is not the site. */}
      <div className="sticky top-0 z-40 flex min-h-11 flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-brand px-4 py-2 text-center text-sm text-black">
        <span>Preview of whitelabels.com/contact-us. A form sent here makes a real lead in Prisma.</span>
        <span className="flex gap-4">
          {sent && (
            <Link to={`/leads/${encodeURIComponent(sent.result.lead_id)}`} className="font-bold underline underline-offset-2">
              Open the lead
            </Link>
          )}
          <Link to="/" className="font-bold underline underline-offset-2">
            Back to Prisma
          </Link>
        </span>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand/25 blur-[140px]" aria-hidden />
        <div className="pointer-events-none absolute -right-32 top-24 h-[420px] w-[420px] rounded-full bg-brand/20 blur-[140px]" aria-hidden />

        <header className="relative mx-auto flex max-w-[1240px] items-center justify-between gap-6 px-4 py-6 md:px-8">
          <img src="/assets/site-preview/WL-Logo-wide-white.svg" alt="Whitelabels.com" className="h-6 w-auto md:h-7" />
          <nav className="hidden items-center gap-7 text-sm text-white/85 lg:flex" aria-label="Site">
            {['Product', 'Templates', 'Marketplace', 'Pricing', 'Resources'].map((n) => (
              <span key={n}>{n}</span>
            ))}
            <span className="relative text-white">
              Contact
              <span className="absolute -bottom-2 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-brand" />
            </span>
            <span className="h-5 w-px bg-white/20" />
            <span>Why we exist</span>
          </nav>
          <span className="inline-flex h-10 items-center rounded-full px-5 text-sm ring-1 ring-white/80">Login</span>
        </header>

        <section className="relative mx-auto max-w-3xl px-4 pb-24 pt-12 text-center md:pb-32 md:pt-16">
          <h1 className="text-[2.6rem] leading-[1.05] tracking-tight md:text-7xl">
            <span className="block font-light">Start the conversation.</span>
            <span className="block bg-gradient-to-r from-brand via-brand to-site-gold bg-clip-text pb-1 font-bold text-transparent">Own the house.</span>
          </h1>
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
            Whether you’re launching your first brand or scaling an operation, tell us what you’re building. A real person from the team will map out how Whitelabels.com gets you live.
          </p>
        </section>
      </div>

      <main className="relative rounded-t-[28px] bg-site-sheet text-site-ink md:rounded-t-[40px]">
        <div className="mx-auto grid max-w-[1240px] gap-12 px-4 py-14 md:px-8 md:py-20 lg:grid-cols-[1fr_1.12fr] lg:gap-16">
          <aside className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand">Get in touch</p>
            <h2 className="mt-4 text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
              Tell us about
              <br />
              your project.
            </h2>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-site-text">
              Drop your details and a note. A real person from the team reads every message and replies personally.
            </p>
            <ul className="mt-8 grid max-w-md gap-3">
              <Channel icon={<MailIcon />} label="Email" value="contact@whitelabels.com" href="mailto:contact@whitelabels.com" />
              <Channel icon={<MailIcon />} label="Partnerships" value="partnerships@whitelabels.com" href="mailto:partnerships@whitelabels.com" />
              <Channel
                icon={<TelegramIcon />}
                label="Telegram"
                value="Chat with Assistant, our AI bot"
                note={telegram ? 'Answers right away' : 'Preview: Assistant’s account is not connected here'}
                href={telegram || undefined}
                tone="telegram"
              />
            </ul>
            <ul className="mt-8 grid max-w-md gap-3 text-[15px] text-site-text">
              <li className="flex gap-3">
                <Check />
                <span>
                  <strong className="text-site-ink">Replies within one business day.</strong> Usually much sooner.
                </span>
              </li>
              <li className="flex gap-3">
                <Check />
                <span>
                  <strong className="text-site-ink">Straight to the team.</strong> No call-centre, no ticket queue.
                </span>
              </li>
              <li className="flex gap-3">
                <Check />
                <span>
                  <strong className="text-site-ink">Always clear who writes.</strong> On Telegram, Assistant is the bot; people write from their own accounts.
                </span>
              </li>
            </ul>
          </aside>

          <div className={classNames(card, 'min-w-0 rounded-3xl p-6 md:p-10')}>
            {sent ? (
              <Sent first={sent.first} result={sent.result} onAgain={() => setSent(null)} />
            ) : (
              <>
                <h3 className="text-2xl font-bold">Send us a message</h3>
                <p className="mt-1 text-sm text-site-muted">
                  Fields marked <span className="font-black text-brand">*</span> are required.
                </p>
                {alert && (
                  <p role="alert" className="mt-5 rounded-xl bg-danger/10 px-4 py-3 text-sm text-site-ink">
                    {alert}
                  </p>
                )}
                <form onSubmit={submit} noValidate className="mt-7 grid gap-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field id="cf-first_name" label="First name" error={errors.first_name}>
                      <input id="cf-first_name" className={input} value={form.first_name} onChange={set('first_name')} autoComplete="given-name" {...described('first_name')} />
                    </Field>
                    <Field id="cf-last_name" label="Last name" error={errors.last_name}>
                      <input id="cf-last_name" className={input} value={form.last_name} onChange={set('last_name')} autoComplete="family-name" {...described('last_name')} />
                    </Field>
                  </div>
                  <Field id="cf-email" label="Email" error={errors.email}>
                    <input id="cf-email" type="email" inputMode="email" className={input} value={form.email} onChange={set('email')} autoComplete="email" {...described('email')} />
                  </Field>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field id="cf-phone" label="Phone" optional>
                      <input id="cf-phone" type="tel" inputMode="tel" className={input} value={form.phone} onChange={set('phone')} autoComplete="tel" placeholder="+1 555 000 0000" />
                    </Field>
                    <Field id="cf-telegram" label="Telegram" optional>
                      <input id="cf-telegram" className={input} value={form.telegram} onChange={set('telegram')} autoComplete="off" placeholder="@username" />
                    </Field>
                  </div>
                  <Field id="cf-heard_from" label="How did you hear about us?" optional>
                    <select id="cf-heard_from" className={classNames(input, 'appearance-none bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-10', !form.heard_from && 'text-site-muted')} value={form.heard_from} onChange={set('heard_from')}>
                      <option value="">Select an option…</option>
                      {HEARD.map((h) => (
                        <option key={h}>{h}</option>
                      ))}
                    </select>
                  </Field>
                  <Field id="cf-message" label="Message" error={errors.message}>
                    <textarea
                      id="cf-message"
                      rows={5}
                      className={classNames(input, 'h-auto py-3 leading-relaxed')}
                      value={form.message}
                      onChange={set('message')}
                      placeholder="Tell us what you’re launching, where, and what you need from a platform."
                      {...described('message')}
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={busy}
                    className="mt-1 inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-brand text-base font-bold text-black shadow-[0_10px_30px_-10px_rgb(255_121_27/0.8)] transition hover:bg-site-ink hover:text-white active:translate-y-px disabled:opacity-60"
                  >
                    {busy ? 'Sending…' : 'Send message'}
                    {!busy && (
                      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    )}
                  </button>
                  <p className="text-center text-xs text-site-muted">By sending this message you agree to our Privacy Policy. We only use your details to reply to your enquiry.</p>
                </form>
              </>
            )}
          </div>
        </div>

        <div className="mx-auto grid max-w-[1240px] gap-4 px-4 pb-20 md:grid-cols-3 md:px-8">
          {[
            ['Fast, human replies', 'A senior team member responds personally, usually within one business day.'],
            ['No sales pressure', 'A straight conversation about fit: no aggressive funnels, no drip spam.'],
            ['Built for operators', 'We speak iGaming: payments, content, compliance and gamification. Bring the hard questions.'],
          ].map(([t, d]) => (
            <div key={t} className={classNames(card, 'p-6')}>
              <h4 className="font-bold">{t}</h4>
              <p className="mt-1.5 text-sm leading-relaxed text-site-text">{d}</p>
            </div>
          ))}
        </div>
      </main>

      <footer className="mx-auto max-w-[1240px] px-4 py-12 text-sm text-white/55 md:px-8">
        <img src="/assets/site-preview/WL-Logo-wide-white.svg" alt="" className="h-5 w-auto" />
        <p className="mt-8">© Copyright Whitelabels.com. All rights reserved.</p>
        <p className="mt-3 max-w-4xl leading-relaxed">
          Whitelabels.com is a B2B iGaming technology provider. Our products and services are offered exclusively to gambling operators, not to consumers, and no gambling activity takes place on this website.
        </p>
      </footer>
    </div>
  );
};

export default SitePreviewPage;
